import { oc } from "@orpc/contract";
import { z } from "zod";
import { deleteUploadToolRecordContract } from "./upload-layouts.contract";
import {
  FeedbackSupplierSchema,
  HUBBELL_FEEDBACK_DIVISIONS,
} from "./hubbell-feedback.contract";

/**
 * Renewal Rules for the Mass Renewal upload (docs/pricing/mass-renewal-upload.md):
 * the pricing team's standing exceptions to a Feedback File's Margin Increase
 * and Core Cost Increase, kept per organization so each renewal reuses them.
 *
 * A rule is one kind with its parameters, scoped to a supplier and to some
 * Divisions (none = every Division). The run applies the enabled rules in
 * scope to each line's adjustment before pricing it. Adding a kind is one
 * member here, one pure function in the run's `renewalRules.ts`, one form on
 * the page.
 */

const DIVISION_LETTERS = HUBBELL_FEEDBACK_DIVISIONS.map(
  (division) => division.letter,
);

/** A Division by its tab letter in the Results Workbook ("T" = N. West). */
export const RenewalRuleDivisionSchema = z
  .string()
  .refine((letter) => DIVISION_LETTERS.includes(letter), {
    message: "Unknown Division",
  });

/** The largest item list a rule stores; the real sensitive lists hold 10–30. */
export const RENEWAL_RULE_MAX_ITEMS = 20_000;

export const RenewalRuleKindSchema = z.discriminatedUnion("type", [
  /**
   * The supplier did not move the cost: neither increase applies, so the sell
   * stays where it was ("our customers will catch us if we move an item the
   * supplier didn't"). Unchanged = within `tolerance` dollars.
   */
  z.object({
    type: z.literal("holdWhenCostUnchanged"),
    tolerance: z.number().finite().min(0).max(1),
  }),
  /**
   * A decrease never takes the margin below `floor` (a fraction, 0.1 = 10%);
   * a line already below it is not decreased. Increases are untouched.
   */
  z.object({
    type: z.literal("marginFloor"),
    floor: z.number().finite().min(0).max(0.99),
  }),
  /** Listed AS400 items (a Division's sensitive list) get neither increase. */
  z.object({
    type: z.literal("holdListedItems"),
    items: z
      .array(z.string().trim().min(1).max(40))
      .min(1)
      .max(RENEWAL_RULE_MAX_ITEMS),
  }),
]);

export const RENEWAL_RULE_TYPES = [
  "holdWhenCostUnchanged",
  "marginFloor",
  "holdListedItems",
] as const satisfies readonly z.infer<typeof RenewalRuleKindSchema>["type"][];

const RenewalRuleNameSchema = z.string().trim().min(1).max(120);
const RenewalRuleDivisionsSchema = z
  .array(RenewalRuleDivisionSchema)
  .max(DIVISION_LETTERS.length);

export const RenewalRuleSchema = z.object({
  id: z.string().uuid(),
  name: RenewalRuleNameSchema,
  supplier: FeedbackSupplierSchema,
  /** Tab letters; empty = every Division. */
  divisions: RenewalRuleDivisionsSchema,
  rule: RenewalRuleKindSchema,
  enabled: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const CreateRenewalRuleInputSchema = z.object({
  name: RenewalRuleNameSchema,
  supplier: FeedbackSupplierSchema,
  divisions: RenewalRuleDivisionsSchema.default([]),
  rule: RenewalRuleKindSchema,
  enabled: z.boolean().default(true),
});

export const UpdateRenewalRuleInputSchema = z.object({
  id: z.string().uuid(),
  name: RenewalRuleNameSchema.optional(),
  divisions: RenewalRuleDivisionsSchema.optional(),
  rule: RenewalRuleKindSchema.optional(),
  enabled: z.boolean().optional(),
});

export const renewalRulesContracts = {
  list: oc
    .input(z.object({}))
    .output(
      z.object({
        rules: z.array(RenewalRuleSchema),
        /**
         * Stored rules this contract can no longer read. Reported rather than
         * dropped, so a run is never priced against half the rules.
         */
        unreadableRuleIds: z.array(z.string().uuid()),
      }),
    )
    .route({
      method: "GET",
      summary: "List the org's Mass Renewal rules",
      description:
        "Returns the organization's Renewal Rules for the Hubbell and ABB Mass Renewal upload (hold when cost unchanged, margin floor, hold listed items), each scoped to a supplier and Divisions, plus ids of unreadable stored rules.",
    }),
  create: oc
    .input(CreateRenewalRuleInputSchema)
    .output(z.object({ rule: RenewalRuleSchema }))
    .route({
      method: "POST",
      summary: "Create a Mass Renewal rule",
      description:
        "Adds a Renewal Rule for one supplier, optionally limited to some Divisions by tab letter. Rules adjust a Feedback File's margin and core cost increases before the upload is priced.",
    }),
  update: oc
    .input(UpdateRenewalRuleInputSchema)
    .output(z.object({ rule: RenewalRuleSchema }))
    .route({
      method: "PATCH",
      summary: "Update a Mass Renewal rule",
      description:
        "Changes a Renewal Rule's name, Divisions, parameters (e.g. the margin floor or the item list) or enabled flag. The supplier is fixed at creation.",
    }),
  delete: deleteUploadToolRecordContract(
    "Delete a Mass Renewal rule",
    "Permanently removes one Renewal Rule from the organization.",
  ),
} as const;

export type RenewalRuleKind = z.infer<typeof RenewalRuleKindSchema>;
export type RenewalRuleType = RenewalRuleKind["type"];
export type RenewalRule = z.infer<typeof RenewalRuleSchema>;
export type CreateRenewalRuleInput = z.infer<
  typeof CreateRenewalRuleInputSchema
>;
export type UpdateRenewalRuleInput = z.infer<
  typeof UpdateRenewalRuleInputSchema
>;
